import { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { calcBetProfit, normalizeBetLimits } from "./clientUtils";

const QUICK_STAKES = [100, 200, 500, 1000, 2000, 5000];

const ClientBetSlip = ({
  selection,
  betType,
  odds,
  marketType = "bookmaker",
  minBet = 100,
  maxBet = 200000,
  balance = 0,
  placing = false,
  compact = false,
  onCancel,
  onPlace,
}) => {
  const { min, max } = useMemo(
    () => normalizeBetLimits(minBet, maxBet),
    [minBet, maxBet]
  );
  const bal = Number(balance) || 0;
  // Cap by balance, but never by a broken provider max of 1
  const ceiling = Math.max(min, Math.min(max, bal > 0 ? bal : max));

  const [stakeText, setStakeText] = useState(String(min));
  const [touched, setTouched] = useState(false);
  const slipIdRef = useRef("");

  useEffect(() => {
    const slipId = `${selection}|${betType}`;
    if (slipIdRef.current === slipId) return;
    slipIdRef.current = slipId;
    setStakeText(String(min));
    setTouched(false);
  }, [selection, betType, min]);

  const stake = Number(stakeText);
  const stakeNum = Number.isFinite(stake) ? stake : 0;

  const stakeError = useMemo(() => {
    if (stakeText.trim() === "" || !Number.isFinite(stake)) return "Enter stake amount";
    if (stakeNum < min) return `Minimum stake is ${min}`;
    if (stakeNum > max) return `Maximum stake is ${max}`;
    if (bal > 0 && stakeNum > bal) return `Insufficient balance (${bal.toFixed(2)})`;
    return "";
  }, [stakeText, stake, stakeNum, min, max, bal]);

  if (!selection) return null;

  const potentialWin =
    stakeNum && odds ? calcBetProfit(stakeNum, odds, marketType, betType) : "0.00";
  const modeLabel = betType === "back" ? "LAGAI" : "KHAI";
  const canPlace = !stakeError && !placing;

  const setStakeValue = (value) => {
    const n = Math.floor(Number(value) || 0);
    const capped = Math.max(0, Math.min(n, ceiling));
    setStakeText(String(capped));
    setTouched(true);
  };

  const addStake = (amount) => {
    const next = (Number.isFinite(stakeNum) ? stakeNum : 0) + Number(amount);
    setStakeValue(next);
  };

  const handlePlace = () => {
    setTouched(true);
    if (stakeError) {
      message.warning(stakeError);
      return;
    }
    onPlace(stakeNum);
  };

  return (
    <div className={`play-bet-slip ${compact ? "inline" : ""}`}>
      <div className="play-bet-slip-header">
        <span>
          {selection} · {modeLabel} @ {odds}
        </span>
        <button type="button" className="play-slip-x" onClick={onCancel} aria-label="Close">
          ×
        </button>
      </div>

      <div className={`play-stake-row ${touched && stakeError ? "has-error" : ""}`}>
        <div className="play-odds-box">{odds}</div>
        <input
          className="play-stake-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={stakeText}
          placeholder="Stake"
          onChange={(e) => {
            const raw = e.target.value.replace(/[^\d]/g, "");
            setTouched(true);
            setStakeText(raw);
          }}
          onBlur={() => setTouched(true)}
        />
      </div>
      {touched && stakeError ? <div className="field-error slip">{stakeError}</div> : null}

      <div className="play-quick-grid compact">
        {QUICK_STAKES.map((amt) => (
          <button key={amt} type="button" className="play-quick-btn" onClick={() => addStake(amt)}>
            +{amt}
          </button>
        ))}
        <button type="button" className="play-quick-btn" onClick={() => setStakeValue(min)}>
          Min
        </button>
        <button type="button" className="play-quick-btn" onClick={() => setStakeValue(ceiling)}>
          Max
        </button>
      </div>

      <div className="play-win-line">
        Profit <strong>+{potentialWin}</strong>
        <span>
          {" "}
          · Bal {bal.toFixed(0)} · Max {Math.floor(ceiling)}
        </span>
      </div>

      <div className="play-final-btns">
        <button type="button" className="play-cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="play-place-btn"
          disabled={!canPlace}
          onClick={handlePlace}>
          {placing ? "…" : "Place bet"}
        </button>
      </div>
    </div>
  );
};

export default ClientBetSlip;
